import { config } from "../Environment";

/**
 * Mental Model Engine API — objects, visits, operator application, AI assist.
 * Backend: ClaryNext-BE /api/mm/* (see planning/features/mental-model-engine.md)
 */

export type MmRegister = "plain" | "guided" | "expert";

export type MmObjectType =
  | "belief" | "fear" | "goal" | "value" | "attitude" | "self_image"
  | "assumption" | "fact" | "conflict" | "memory" | "proxy" | "theory"
  | "decision" | "other";

export type MmOrientation = "approach" | "neutral" | "avoid";

export interface MmProfile {
  frame?: string;
  resolution?: number;      // 1-5
  complexity?: number;      // 1-5
  boundaries?: number;      // 1-5
  certainty?: number;       // 1-5
  charge?: number;          // -5..5
  orientation?: MmOrientation;
  identification?: number;  // 1-5
  malleability?: number;    // 1-5
}

export type MmProfileSource = Partial<Record<keyof MmProfile, "user" | "ai" | "prior">>;

export interface MmObject {
  _id: string;
  fiUser: string;
  label?: string;
  statement: string;
  workingStatement?: string;
  objectType: MmObjectType;
  profile: MmProfile;
  profileSource: MmProfileSource;
  status: "active" | "parked" | "retired" | "superseded";
  activeGoal?: {
    statedOutcome?: string;
    targetState?: MmProfile;
    route?: string[];
    setOn?: string;
  };
  parkedUntil?: string;
  parkedCondition?: string;
  liftedFrom?: { entityType: string; entityId: string };
  /** Life areas (IdentitySpace ids) the person filed this model into. */
  spaceIds?: string[];
  /** Where it was created; captureId when it came from a smart capture. */
  context?: { surface?: string; captureId?: string };
  links: Array<{ fiObject: string; linkType: string; note?: string }>;
  visibility: "private" | "mentor";
  visitCount: number;
  lastVisitedOn?: string;
  createdOn: string;
  lastUpdated: string;
}

export interface MmAppraisal {
  importance?: number;
  urgency?: number;
  interest?: number;
  influencability?: number;
  risk?: number;
  difficulty?: number;
}

export interface MmVisit {
  _id: string;
  fiObject: string;
  register: MmRegister;
  appraisal?: MmAppraisal;
  statedOutcome?: string;
  targetState?: MmProfile;
  plannedRoute: Array<{ operatorId: string; status: "pending" | "done" | "skipped" }>;
  status: "open" | "completed" | "abandoned";
  startedOn: string;
}

export interface MmMenu {
  allowed: string[];
  blocked?: Record<string, string>;
  forcedFirst?: string | null;
}

export interface MmEvent {
  _id: string;
  kind:
    | "captured"
    | "profile"
    | "operator"
    | "status"
    | "link"
    | "writeback"
    | "emit"
    | "goal"
    | "visit";
  payload: any;
  createdOn: string;
}

export interface MmApplyResult {
  event: MmEvent;
  object: MmObject;
  emitted: Array<{ artifactType: string; entityId: string }>;
  children: MmObject[];
  delta: { before: MmProfile; after: MmProfile };
  menu: MmMenu;
  warnings: string[];
}

/**
 * FE mirror of the operator registry's presentation facts. The SERVER decides
 * what is allowed (menu comes from /api/mm/visits); this only tells the UI how
 * to render each operator. Keep in sync with BE utils/mmRegistry.js.
 */
export const OPERATOR_META: Record<
  string,
  { family: string; interaction: string; aiAssist: boolean }
> = {
  externalize: { family: "create", interaction: "capture", aiAssist: false },
  name: { family: "create", interaction: "generate_choose", aiAssist: true },
  reframe: { family: "create", interaction: "generate_choose", aiAssist: true },
  bound: { family: "create", interaction: "freetext_guided", aiAssist: true },
  split: { family: "create", interaction: "split_board", aiAssist: true },
  concretize: { family: "form", interaction: "freetext_guided", aiAssist: true },
  decompose: { family: "form", interaction: "split_board", aiAssist: true },
  question: { family: "epistemic", interaction: "freetext_guided", aiAssist: true },
  doubt: { family: "epistemic", interaction: "freetext_guided", aiAssist: true },
  test: { family: "epistemic", interaction: "external_commit", aiAssist: true },
  park: { family: "epistemic", interaction: "freetext_guided", aiAssist: false },
  distance: { family: "affect", interaction: "freetext_guided", aiAssist: true },
  retire: { family: "create", interaction: "freetext_guided", aiAssist: true },
  simplify: { family: "form", interaction: "freetext_guided", aiAssist: true },
  elaborate: { family: "form", interaction: "freetext_guided", aiAssist: true },
  contextualize: { family: "relations", interaction: "freetext_guided", aiAssist: true },
  deduce: { family: "epistemic", interaction: "freetext_guided", aiAssist: true },
  simulate: { family: "epistemic", interaction: "freetext_guided", aiAssist: true },
  analogize: { family: "relations", interaction: "generate_choose", aiAssist: true },
  accept_oppose: { family: "affect", interaction: "generate_choose", aiAssist: true },
  own_disown: { family: "affect", interaction: "freetext_guided", aiAssist: true },
  amplify: { family: "affect", interaction: "freetext_guided", aiAssist: true },
  compare: { family: "relations", interaction: "compare_grid", aiAssist: true },
  link: { family: "relations", interaction: "link_picker", aiAssist: false },
  map: { family: "relations", interaction: "territory_map", aiAssist: true },
  // ICDF design family (registry v2) — building structures, not just examining
  dsn_research: { family: "design", interaction: "guided_steps", aiAssist: true },
  dsn_define: { family: "design", interaction: "define_frame", aiAssist: true },
  dsn_criteria: { family: "design", interaction: "checklist_board", aiAssist: true },
  dsn_generate: { family: "design", interaction: "checklist_board", aiAssist: true },
  dsn_synthesize: { family: "design", interaction: "design_matrix", aiAssist: true },
  dsn_mental_test: { family: "design", interaction: "guided_steps", aiAssist: true },
  dsn_harden: { family: "design", interaction: "checklist_board", aiAssist: true },
  dsn_premortem: { family: "design", interaction: "checklist_board", aiAssist: true },
  dsn_implement: { family: "design", interaction: "design_commit", aiAssist: true },
};

// ICDF §2 — why the engine was opened (friction vs plan execution)
export interface MmVisitReason {
  kind: "friction" | "plan";
  frictionType?: "external" | "internal";
  planRef?: { pwfId?: string; phaseId?: string; processId?: string };
  note?: string;
}

// ICDF §4 — operator mastery, derived from mm_events
export type MmMasteryLevel = "new" | "tried" | "practicing" | "fluent";
export interface MmOperatorMastery {
  operatorId: string;
  family: string;
  uses: number;
  distinctObjects: number;
  lastUsed: string | null;
  level: MmMasteryLevel;
}

export async function getMastery(
  retryFetch: Function
): Promise<{
  status: string;
  operators: MmOperatorMastery[];
  reasonMix: { friction: number; plan: number };
}> {
  const response = await retryFetch(`${base()}/api/mm/mastery`, {
    credentials: "include",
  });
  return ok(response, "Load mastery");
}

export const LINK_TYPES = [
  "causes", "enables", "blocks", "protects", "contradicts", "competes",
  "is_part_of", "is_evidence_for", "analogous_to", "supersedes",
] as const;

const base = () => config.API_BASE;

const jsonHeaders = { "Content-Type": "application/json" };

async function ok(response: Response, what: string) {
  if (!response.ok) {
    let fault = response.statusText;
    try {
      const body = await response.json();
      if (body && body.fault) fault = body.fault;
      // 422 guard rejections carry a menu — surface the whole body
      if (response.status === 422) return body;
    } catch {
      /* noop */
    }
    throw new Error(`${what} failed: ${fault}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

export async function createObject(
  retryFetch: Function,
  data: { statement: string; objectType?: MmObjectType; label?: string }
): Promise<{ status: string; object: MmObject }> {
  const response = await retryFetch(`${base()}/api/mm/objects`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({ ...data, context: { surface: "standalone" } }),
  });
  return ok(response, "Create object");
}

export async function listObjects(
  retryFetch: Function,
  params?: { status?: string; sort?: "attention"; spaceId?: string }
): Promise<{ status: string; objects: MmObject[] }> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.spaceId) q.set("spaceId", params.spaceId);
  if (params?.sort) q.set("sort", params.sort);
  const response = await retryFetch(`${base()}/api/mm/objects?${q.toString()}`, {
    credentials: "include",
  });
  return ok(response, "List objects");
}

export async function updateObject(
  retryFetch: Function,
  objectId: string,
  data: { spaceIds?: string[] }
): Promise<{ status: string; object: MmObject }> {
  const response = await retryFetch(`${base()}/api/mm/objects/${objectId}`, {
    method: "PATCH",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Update object");
}

export type MmCapturedWithKind = "insight" | "action" | "mental_model" | "project" | "quality";

export type MmOrigin =
  | { kind: "capture"; captureId: string; date?: number; text?: string }
  | { kind: "user_takeaway" | "user_trait"; id: string; text?: string; date?: number };

export interface MmObjectContext {
  status: string;
  origin: MmOrigin | null;
  capturedWith: Array<{ kind: MmCapturedWithKind; id: string; text: string }>;
  spaces: Array<{ id: string; name: string }>;
  links: Array<{ id: string; label: string; objectType: string; linkType: string; incoming?: boolean }>;
}

/** Where a model came from, what was captured with it, its areas and links. */
export async function getObjectContext(
  retryFetch: Function,
  objectId: string
): Promise<MmObjectContext> {
  const response = await retryFetch(`${base()}/api/mm/objects/${objectId}/context`, {
    credentials: "include",
  });
  return ok(response, "Object context");
}

export interface MmUnderneathItem {
  id: string;
  label: string;
  objectType: string;
}

/** Server cap on ids per /underneath request. */
export const UNDERNEATH_MAX_IDS = 100;

/** Models "underneath" each entity (insight, step, quality, project). Absent key = none. */
export async function getUnderneath(
  retryFetch: Function,
  ids: string[]
): Promise<{ status: string; byId: Record<string, MmUnderneathItem[]> }> {
  const q = new URLSearchParams({ ids: ids.join(",") });
  const response = await retryFetch(`${base()}/api/mm/underneath?${q.toString()}`, {
    credentials: "include",
  });
  return ok(response, "Underneath");
}

export async function getBiography(
  retryFetch: Function,
  objectId: string
): Promise<{
  status: string;
  object: MmObject;
  events: MmEvent[];
  series: Record<string, Array<{ t: string; v: number }>>;
}> {
  const response = await retryFetch(`${base()}/api/mm/objects/${objectId}/biography`, {
    credentials: "include",
  });
  return ok(response, "Get biography");
}

export async function snapshotProfile(
  retryFetch: Function,
  objectId: string,
  data: { profile: MmProfile; profileSource?: MmProfileSource; trigger?: string; fiVisit?: string }
): Promise<{ status: string; object: MmObject }> {
  const response = await retryFetch(`${base()}/api/mm/objects/${objectId}/profile`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Save profile");
}

export async function changeStatus(
  retryFetch: Function,
  objectId: string,
  data: {
    status: "active" | "parked" | "retired" | "superseded";
    parkedUntil?: string;
    parkedCondition?: string;
    retiredNote?: string;
  }
): Promise<{ status: string; object: MmObject }> {
  const response = await retryFetch(`${base()}/api/mm/objects/${objectId}/status`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Change status");
}

export async function liftEntity(
  retryFetch: Function,
  data: { entityType: "user_takeaway" | "user_trait"; entityId: string; objectType?: MmObjectType }
): Promise<{ status: string; object: MmObject; existed: boolean }> {
  const response = await retryFetch(`${base()}/api/mm/lift`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Lift");
}

export interface MmPath {
  id: string;
  kind: "script" | "template";
  name?: string; // templates only; script names come from i18n
  description?: string;
  steps: string[];
  objectTypes: string[];
  isMine?: boolean;
}

export async function listPaths(
  retryFetch: Function
): Promise<{ status: string; scripts: MmPath[]; templates: MmPath[] }> {
  const response = await retryFetch(`${base()}/api/mm/paths`, { credentials: "include" });
  return ok(response, "List paths");
}

export async function createTemplate(
  retryFetch: Function,
  data: { name: string; description?: string; operatorIds: string[]; objectTypes?: string[]; isPublic?: boolean }
): Promise<{ status: string; template: any }> {
  const response = await retryFetch(`${base()}/api/mm/templates`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Create template");
}

export async function writebackPreview(
  retryFetch: Function,
  objectId: string
): Promise<{
  status: string;
  entityType: string;
  takeawayType: "action" | "insight";
  current: string;
  proposed: string;
  hasChange: boolean;
}> {
  const response = await retryFetch(`${base()}/api/mm/objects/${objectId}/writeback`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({}),
  });
  return ok(response, "Write-back preview");
}

export async function writebackConfirm(
  retryFetch: Function,
  objectId: string,
  data: { decision: "accepted" | "declined"; proposed?: string }
): Promise<{ status: string; applied: boolean }> {
  const response = await retryFetch(`${base()}/api/mm/objects/${objectId}/writeback/confirm`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Write-back confirm");
}

// ---------------------------------------------------------------------------
// Visits
// ---------------------------------------------------------------------------

export async function openVisit(
  retryFetch: Function,
  data: {
    fiObject: string;
    register?: MmRegister;
    appraisal?: MmAppraisal;
    statedOutcome?: string;
    targetState?: MmProfile;
    pathId?: string; // builtin script id or template id — a predefined path
    allowedOperators?: string[]; // host restriction (activity/meeting embeds)
    reason?: MmVisitReason; // ICDF: why the engine was opened
    context?: { surface: string };
  }
): Promise<{
  status: string;
  visit: MmVisit;
  menu: MmMenu;
  warnings: string[];
  plan: { route: string[]; rationale: any[]; honesty: any } | null;
  /** Present when a reason was given; frictionVisits counts this visit too. */
  reasonStats?: { frictionVisits: number };
}> {
  const response = await retryFetch(`${base()}/api/mm/visits`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({ context: { surface: "standalone" }, ...data }),
  });
  return ok(response, "Open visit");
}

export async function applyOperator(
  retryFetch: Function,
  visitId: string,
  data: {
    operatorId: string;
    input?: any;
    output?: any;
    profileAfter?: MmProfile;
    proposedBy?: "user" | "planner" | "ai";
  }
): Promise<MmApplyResult & { status: string; fault?: string }> {
  const response = await retryFetch(`${base()}/api/mm/visits/${visitId}/apply`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Apply operator");
}

export async function closeVisit(
  retryFetch: Function,
  visitId: string,
  status: "completed" | "abandoned"
): Promise<{ status: string; visit: MmVisit }> {
  const response = await retryFetch(`${base()}/api/mm/visits/${visitId}/close`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({ status }),
  });
  return ok(response, "Close visit");
}

// ---------------------------------------------------------------------------
// AI
// ---------------------------------------------------------------------------

export async function inferProfile(
  retryFetch: Function,
  data: { statement: string; objectType?: MmObjectType; language?: string }
): Promise<{
  status: string;
  distress: boolean;
  profile?: MmProfile;
  confidence?: Record<string, number>;
}> {
  const response = await retryFetch(`${base()}/api/mm/ai/infer-profile`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Infer profile");
}

export async function aiTargetState(
  retryFetch: Function,
  data: { statedOutcome: string; fiObject: string; language?: string; register?: MmRegister }
): Promise<{ status: string; targetState: MmProfile; restatement: string }> {
  const response = await retryFetch(`${base()}/api/mm/ai/target-state`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Target state");
}

export async function operatorAssist(
  retryFetch: Function,
  data: {
    operatorId: string;
    fiObject: string;
    language?: string;
    register?: MmRegister;
    extra?: any; // operator-specific context (compare: {target})
  }
): Promise<{ status: string; assist: any }> {
  const response = await retryFetch(`${base()}/api/mm/ai/operator-assist`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Operator assist");
}

// ---------------------------------------------------------------------------
// ICDF §5 — the two formative loops (Autoenginuity + Reality-Shaping).
// Both endpoints only propose; adopting anything is a separate user action.
// ---------------------------------------------------------------------------

export interface AutoenginuityGap {
  name: string;
  emoji?: string;
  description?: string;
  why?: string;
}

export async function autoenginuityGaps(
  retryFetch: Function,
  data: { worked?: string; failed?: string; missing?: string; language?: string; register?: MmRegister }
): Promise<{ status: string; gaps: AutoenginuityGap[] }> {
  const response = await retryFetch(`${base()}/api/mm/ai/autoenginuity-gaps`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Autoenginuity gaps");
}

export interface RealityShapingTheme {
  theme: string;
  whatItAsks: string;
  evidence: string;
  route: "learn" | "gap" | "design";
  searchQuery?: string;
}

export async function realityShaping(
  retryFetch: Function,
  data: { periodDays?: number; language?: string; register?: MmRegister }
): Promise<{
  status: string;
  themes: RealityShapingTheme[];
  counts: { frictions: number; reflections: number; insights: number };
  periodDays?: number;
}> {
  const response = await retryFetch(`${base()}/api/mm/ai/reality-shaping`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(data),
  });
  return ok(response, "Reality shaping");
}
